/**
 * What one subject row is allowed to say about itself. F-158.
 *
 * **Its own module with no DOM and no React in it, for `plan/view.ts`'s reason**: `npm test` does
 * not type-check `.tsx` at all — `tsconfig.test.json` compiles a handful of React-free modules
 * and nothing else — so a judgement written inline in `Research.tsx` is a judgement no test in
 * this repository can see. Every decision below was got wrong on the screen this replaces, and
 * none of the three ways of getting it wrong would raise anything.
 *
 * Leo, 2026-09-19, walking a new learner's flow, in three separate complaints:
 *
 * > *"what research found says 2730 characters but each section already has dozens of thousands"*
 *
 * > *"little found vs researched is not quite effective, shouldn't say little found or not, also
 * > number of characters is quite irrelevant, can we do number of sources?"*
 *
 * > *"researched doesn't show those buttons, there is no reason to believe i wont want to add my
 * > own material to a researched subject, again the little found vs researched is meaningless,
 * > its highly subject dependent (5000 characters may be a LOT for some obscure things) and we
 * > cant make a judgement of where we allow the user to modify their plan."*
 *
 * Three rules come out of that, and this module is all three of them:
 *
 * 1. **No `thin`/`deep`/`barren` word reaches a learner.** They are a judgement about whether a
 *    subject found enough, the threshold for enough is entirely subject-dependent, and the
 *    product is not in a position to make the call. `badgeOf` answers `undefined` for all three.
 * 2. **A settled subject is described by how many sources it rests on**, not by how many
 *    characters were written about it. The abstract beside it says what it covers.
 * 3. **Every action is offered on every subject**, so nothing here gates a button. That is not an
 *    omission: the gate this replaces was keyed on the very outcome words rule 1 removes, which
 *    is how a well-researched subject came to be the one subject a learner could not add their
 *    own page to.
 *
 * The copy keys are returned rather than the words, because the words are in two languages and a
 * module a Node test can import cannot reach `i18n.ts` — it imports React. Returning them as a
 * **union of literals** rather than as `string` is what makes that split safe, exactly as
 * `plan/coverage.ts` does it: `CopyKey` is `keyof` the English dictionary, so a key invented here
 * that nobody wrote copy for fails the web build instead of rendering its own name on a phone.
 */

/*
 * Types only, so nothing from `@nabu/shared` reaches the bundle or the Node test's runtime — the
 * barrel re-exports `keys.ts`, which imports `node:crypto`. `plan/carousel.ts` carries the same
 * note. What makes it safe is that a state added to the union with no home below fails the build.
 */
import type { ResearchSubject, ResearchSubjectState } from '@nabu/shared';

/* ------------------------------------------------------------------------- *
 * The badge
 * ------------------------------------------------------------------------- */

/** The copy keys a badge can carry. Each one must exist in both dictionaries. */
export type BadgeCopyKey =
  | 'res.state.outlined'
  | 'res.state.queued'
  | 'res.state.researching'
  | 'res.state.unfunded'
  | 'res.state.failed';

export interface SubjectBadge {
  key: BadgeCopyKey;
  /** `failed` is the only one that may be tinted as a fault. Everything else is a state of play. */
  tone: 'pending' | 'failed';
}

/**
 * The badge for a subject, or **nothing at all once it has been researched**.
 *
 * `deep`, `thin` and `barren` all answer `undefined`, and that is rule 1 above. The three words
 * the screen used to print for them — *researched*, *little found*, *nothing found* — are a
 * verdict on whether what was found is enough, and the same five thousand characters are plenty
 * for an obscure subject and nothing for a broad one. There is no threshold this product can set
 * that is right for both, so it sets none and shows the source count instead.
 *
 * What survives is the states that are facts about *us* rather than about the subject: it has not
 * started, it is being read now, the run did not reach it, the request failed. A learner needs
 * every one of those, and none of them is a judgement.
 */
export function badgeOf(state: ResearchSubjectState): SubjectBadge | undefined {
  switch (state) {
    case 'outlined':
      return { key: 'res.state.outlined', tone: 'pending' };
    case 'queued':
      return { key: 'res.state.queued', tone: 'pending' };
    case 'researching':
      return { key: 'res.state.researching', tone: 'pending' };
    case 'unfunded':
      return { key: 'res.state.unfunded', tone: 'pending' };
    case 'failed':
      return { key: 'res.state.failed', tone: 'failed' };
    case 'deep':
    case 'thin':
    case 'barren':
      return undefined;
  }
}

/* ------------------------------------------------------------------------- *
 * The sources line
 * ------------------------------------------------------------------------- */

/** The copy keys the sources line chooses between. Each one must exist in both dictionaries. */
export type SourcesCopyKey =
  | 'res.sources.none'
  | 'res.sources.one'
  | 'res.sources.many'
  | 'res.sources.unknown';

export interface SourcesLine {
  key: SourcesCopyKey;
  /** Only meaningful for `res.sources.many`, which is the one key with a placeholder. */
  count: number;
}

/**
 * Whether a subject has finished researching, and so has a source count to talk about.
 *
 * Not `SUBJECT_PROGRESS[state] === 1`, which would also catch `unfunded` and `failed` — two
 * states where no pass ever ran and there is therefore nothing to have read. Saying "sources not
 * recorded" about a subject the run never reached would offer an absence as a finding.
 */
const RESEARCHED: ReadonlySet<ResearchSubjectState> = new Set(['deep', 'thin', 'barren']);

/**
 * What a subject says it rests on, or `undefined` when there is nothing honest to say yet.
 *
 * Four answers rather than one number, and the fourth is the one that matters most:
 *
 * - **Still running, or never run** — `undefined`. A subject that has not been researched has no
 *   sources, and printing "0 sources" over a subject that is being read right now would be a
 *   claim about the world rather than about the clock.
 * - **Nought** — `res.sources.none`. The pass looked and read nothing at all. It is a fact about
 *   the subject rather than a failure of ours. Note that this is *not* the same set as the
 *   `barren` subjects: `outcomeOf` calls a subject barren on the verdict alone, so a pass that
 *   read three pages and judged none of them usable is barren with three sources — and says
 *   three, because what this line reports is what was read and not whether it was enough.
 * - **One, or several** — the count.
 * - **Researched, but no count stored** — `res.sources.unknown`. Nothing wrote `sources` before
 *   F-158, so every subject in the table on the day it ships has this and *only* this. Showing
 *   nought for those would tell a learner their twenty-six-source subject rests on none, which is
 *   the exact class of lie this whole change is about; showing nothing at all would leave the row
 *   silently shorter than its neighbours with no reason given. The honest answer is that the pass
 *   that ran did not record it.
 */
export function sourcesLine(subject: {
  state: ResearchSubjectState;
  sources?: number;
}): SourcesLine | undefined {
  if (!RESEARCHED.has(subject.state)) return undefined;
  return sourceCount(subject.sources) ?? { key: 'res.sources.unknown', count: 0 };
}

/**
 * A stored source count as a sentence, or `undefined` when there is no stored count.
 *
 * Split out from `sourcesLine` because **the carousel needs the same four words and knows
 * nothing about subject states**: its cards exist only for subjects that have settled and
 * produced a paragraph, so the "is it researched yet" half of the question is already answered by
 * the card existing. Two copies of this arithmetic is how the plan's researching face and the
 * research section below it would come to phrase the same number differently — which is the
 * disagreement F-129 exists to have ended, in its smallest form.
 *
 * `typeof` rather than `??`, all the way down from the row. `sources: 0` is a number and must
 * reach `res.sources.none`; an absent field must answer `undefined`. A `?? 0` anywhere on this
 * path silently merges the two, and neither the API nor a test would notice.
 */
export function sourceCount(sources: number | undefined): SourcesLine | undefined {
  if (typeof sources !== 'number' || !Number.isFinite(sources) || sources < 0) return undefined;
  if (sources === 0) return { key: 'res.sources.none', count: 0 };
  if (sources === 1) return { key: 'res.sources.one', count: 1 };
  return { key: 'res.sources.many', count: Math.round(sources) };
}

/* ------------------------------------------------------------------------- *
 * The actions
 * ------------------------------------------------------------------------- */

/** The copy keys the second-look button chooses between. */
export type AgainCopyKey = 'cov.remedy.again' | 'res.deeper';

/**
 * What the second-look button is called for this subject.
 *
 * **The button is the same call either way**, and the words say which of two situations the
 * learner is in: *go deeper* is wrong for a subject nothing was found for, because there is
 * nothing to go deeper into, and *look again* is a thin description of asking for more on a
 * subject that already has twenty thousand characters behind it.
 *
 * Keyed on the subject's own state rather than on a coverage verdict, which is the change. The
 * old version asked the report, so a subject with no verdict — the report could not be read, or
 * no plan exists to compare against, which is *every* subject before the first plan is built —
 * fell through to a branch that offered one button instead of two. Rule 3 above is that every
 * subject gets every action, and a rule that depends on a second request having succeeded is not
 * that rule.
 *
 * **Stated as "which states have something to go deeper into", not as a list of the ones that do
 * not** — a review caught the first draft the other way round. `abandon` never settles the
 * subject rows, so after a run that failed or was given up on, a subject can sit at `outlined`,
 * `queued` or `researching` for ever with no document behind it. Named as an exclusion list those
 * three fell through to *go deeper*, offering to deepen something nothing has ever read a word
 * of. Two states have material; everything else is a second look.
 */
export function againLabelFor(state: ResearchSubjectState): AgainCopyKey {
  return state === 'deep' || state === 'thin' ? 'res.deeper' : 'cov.remedy.again';
}

/**
 * Whether this subject's actions are offered at all.
 *
 * One condition, and it is about the **run** rather than about the subject: a pass is in flight,
 * and both actions start or feed a pass. `goDeeper` opens a new run — which the API refuses while
 * one is going — and a page uploaded onto a scoped objective sets research off again, so offering
 * either mid-run is offering a button whose only outcome is a refusal, which is the complaint
 * F-92 came from.
 *
 * Nothing about the subject enters into it. That is the whole of rule 3: the gate this replaces
 * asked the coverage report for a list of *remedies*, and the report returns none for a subject
 * that was researched well and reached the plan — so the one subject a learner could not add
 * their own page to was the one that had gone best.
 */
export function actionsShown(input: { running: boolean }): boolean {
  return !input.running;
}

/* ------------------------------------------------------------------------- *
 * What the whole row says
 * ------------------------------------------------------------------------- */

/** Everything the row is allowed to say about one subject, decided in one place. */
export interface SubjectView {
  badge: SubjectBadge | undefined;
  sources: SourcesLine | undefined;
  again: AgainCopyKey;
  actions: boolean;
}

/**
 * One subject as the screen renders it.
 *
 * A single entry point so that a component cannot take the badge from here and decide the
 * sources line for itself — which is how the screen came to print a character count beside a
 * verdict that disagreed with it.
 */
export function subjectView(
  subject: Pick<ResearchSubject, 'state' | 'sources'>,
  input: { running: boolean },
): SubjectView {
  return {
    badge: badgeOf(subject.state),
    sources: sourcesLine(subject),
    again: againLabelFor(subject.state),
    actions: actionsShown(input),
  };
}
